import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import {
  getCurrentUser,
  login as loginApi,
  logout as logoutApi,
  type AuthUser,
} from "@workspace/api-client-react";
import { queryClient } from "./queryClient";
import { setLang, getLang, isLang } from "@/i18n";
import { setAiProcessingOwner } from "@/lib/ai-processing-state";

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (username: string, password: string, remember?: boolean) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Apply a user returned directly from an auth endpoint that already set the
   * session cookie (verify-email, accept-invite) — avoids an extra round-trip.
   */
  applyUser: (user: AuthUser) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await getCurrentUser();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (isLoading) return;
    setAiProcessingOwner(user?.id ?? null);
  }, [isLoading, user?.id]);

  // For a logged-in user, the account's saved language is authoritative (so the
  // choice follows them across devices). Anonymous visitors keep the language
  // from localStorage / the English default.
  useEffect(() => {
    const preferred = user?.language;
    if (isLang(preferred) && preferred !== getLang()) {
      setLang(preferred);
    }
  }, [user?.language]);

  const login = useCallback(
    async (username: string, password: string, remember?: boolean) => {
      const u = await loginApi({ username, password, remember });
      queryClient.clear();
      setUser(u);
      return u;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      /* ignore */
    }
    queryClient.clear();
    setUser(null);
  }, []);

  const applyUser = useCallback((u: AuthUser) => {
    queryClient.clear();
    setUser(u);
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, login, logout, refresh, applyUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
